import _ from 'lodash';

import { findDriveFamily } from 'utils/contactTypes';

import {
  OVERFLOW_UNKNOWN_SLOTS_MESSAGE,
  PreviewIssueKind,
  UNKNOWN_CONTACT_TYPE,
} from './DrivePreviewPanel.constants';

const getUnmappedTypeKey = ({ contactTypeId, systemKey, contactTypeName }) =>
  contactTypeId ?? systemKey ?? contactTypeName ?? UNKNOWN_CONTACT_TYPE;

const withNames = (message, names) => {
  const knownNames = _.uniq(names.filter(Boolean));

  return knownNames.length ? `${message} (${knownNames.join(', ')})` : message;
};

const getUnmappedIssues = unmapped =>
  Object.values(_.groupBy(unmapped, getUnmappedTypeKey)).map(parties => {
    const [first] = parties;
    const typeName = first.contactTypeName ?? first.systemKey ?? UNKNOWN_CONTACT_TYPE;

    return {
      id: `${PreviewIssueKind.UNMAPPED}|${getUnmappedTypeKey(first)}`,
      kind: PreviewIssueKind.UNMAPPED,
      message: withNames(
        `Contact type '${typeName}' is not mapped to DRIVE`,
        parties.map(({ displayName }) => displayName),
      ),
    };
  });

const getOverflowMessage = ({ familyCode, displayName }, position, slotCount, isFamiliesLoaded) => {
  if (!isFamiliesLoaded) {
    return withNames(OVERFLOW_UNKNOWN_SLOTS_MESSAGE, [displayName]);
  }

  if (!slotCount) {
    return withNames(`No ${familyCode} slot is left in DRIVE`, [displayName]);
  }

  return withNames(
    `${familyCode} ${slotCount + position + 1} exceeds DRIVE's ${slotCount} ${familyCode.toLowerCase()} slots`,
    [displayName],
  );
};

// Slot counts come from the DRIVE families; until they load, the sentence names no family rather
// than claim the family has no slots.
const getOverflowIssues = (overflow, families) => {
  const isFamiliesLoaded = families.length > 0;

  return Object.entries(_.groupBy(overflow, 'familyCode')).flatMap(([familyCode, parties]) => {
    const slotCount = findDriveFamily(families, familyCode)?.slots?.length ?? 0;

    return parties.map((party, position) => ({
      id: `${PreviewIssueKind.OVERFLOW}|${familyCode}|${position}`,
      kind: PreviewIssueKind.OVERFLOW,
      message: getOverflowMessage(party, position, slotCount, isFamiliesLoaded),
    }));
  });
};

/**
 * Everything the preview would leave out or wants checked, as plain sentences. Overflow numbers
 * the extra parties after the family's last slot ("Seller 7 exceeds DRIVE's 6 seller slots").
 * @param {Object|null} preview the drive-report/preview response
 * @param {Array<{ code: string, slots: string[] }>} families DRIVE code families
 * @returns {Array<{ id: string, kind: string, message: string }>}
 */
export const getPreviewIssues = (preview, families) => [
  ...getUnmappedIssues(preview?.unmapped ?? []),
  ...getOverflowIssues(preview?.overflow ?? [], families),
  ...(preview?.warnings ?? []).map((warning, index) => ({
    id: `${PreviewIssueKind.WARNING}|${index}`,
    kind: PreviewIssueKind.WARNING,
    message: warning,
  })),
];

/**
 * @param {Object|null} preview
 * @returns {Array<Object>} rows for the "Will be sent" table
 */
export const getPreviewParticipantRows = preview =>
  (preview?.participants ?? []).map((participant, index) => ({
    id: `${participant.participantType}|${index}`,
    participantType: participant.participantType,
    name:
      [participant.firstName, participant.lastName].filter(Boolean).join(' ') ||
      participant.companyName ||
      '',
    contactTypeName: participant.contactTypeName ?? '',
    email: participant.email ?? '',
  }));

/**
 * @param {number} count
 * @returns {string}
 */
export const getParticipantsTitle = count =>
  count === 1 ? 'Will be sent · 1 participant' : `Will be sent · ${count} participants`;
